import { useImpersonation } from "@/hooks/useImpersonation";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Eye, X, Shield, Loader2 } from "lucide-react";
import { useRef, useEffect } from "react";

const CSS_VAR = "--impersonation-banner-height";

export default function ImpersonationBanner() {
  const { isImpersonating, impersonatedProfile, adminName, stopImpersonation, isStopping } = useImpersonation();
  const navigate = useNavigate();
  const bannerRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    const el = bannerRef.current;
    if (!isImpersonating || !el) {
      document.documentElement.style.setProperty(CSS_VAR, "0px");
      return;
    }

    const update = () => {
      document.documentElement.style.setProperty(CSS_VAR, `${el.offsetHeight}px`);
    };

    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();

    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty(CSS_VAR, "0px");
    };
  }, [isImpersonating]);

  if (!isImpersonating) return null;

  async function handleReturn() {
    const userId = impersonatedProfile?.user_id;
    await stopImpersonation();
    navigate(userId ? `/admin/users/${userId}` : "/admin/users");
  }

  return (
    <div
      ref={bannerRef}
      className="sticky top-0 left-0 right-0 z-[100] flex items-center justify-center gap-3 py-2 px-4 text-sm font-medium shadow-lg"
      style={{ background: "hsl(var(--destructive))", color: "hsl(var(--destructive-foreground))" }}
    >
      <Eye className="h-4 w-4 shrink-0" />
      <span className="flex items-center gap-1.5 flex-wrap justify-center">
        <Shield className="h-3.5 w-3.5" />
        <strong>{adminName}</strong>
        <span>is viewing as</span>
        <strong>{impersonatedProfile?.full_name || "User"}</strong>
        {impersonatedProfile?.email && (
          <span className="opacity-80">({impersonatedProfile.email})</span>
        )}
        <span className="opacity-70">— read-only mode, sensitive actions blocked</span>
      </span>
      <Button
        variant="secondary"
        size="sm"
        className="h-7 gap-1 ml-2 shrink-0"
        onClick={handleReturn}
        disabled={isStopping}
      >
        {isStopping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
        Return to Admin
      </Button>

    </div>
  );
}
